import { piMultipleText } from "@/lib/tool-kit/geometry/angle-input";
import {
  type ExactTrig,
  exactTrigValues,
  formatTrigValue,
  type Quadrant,
  quadrantOf,
  referenceAngle,
  type TrigFunction,
  trigDecimal,
  trigValueNumber,
} from "@/lib/tool-kit/geometry/trig-exact";
import {
  divR,
  intR,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * The angles on a unit-circle chart, with their exact coordinates.
 *
 * "Standard" is the chart in every textbook: multiples of 30° and 45°, sixteen points.
 * "Every 15°" adds the eight angles in between (15°, 75°, 105° and so on), whose values
 * involve √6 − √2.
 */

export type AngleSet = "standard" | "fifteen";

const STANDARD_DEGREES = [
  0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330,
];
const FIFTEEN_DEGREES = Array.from({ length: 24 }, (_, step) => step * 15);

export interface UnitCircleAngle {
  degrees: number;
  /** "π/6", "5π/6", "π", "0". */
  radians: string;
  quadrant: Quadrant;
  /** The acute angle to the x-axis, in degrees. */
  reference: number;
  /** Exact cos, sin and tan as text; "undefined" text is `undefinedText`. */
  cos: string;
  sin: string;
  tan: string;
  /** Decimal values; tan is null on the y-axis. */
  cosValue: number;
  sinValue: number;
  tanValue: number | null;
}

export function angleDegrees(set: AngleSet): number[] {
  return set === "standard" ? STANDARD_DEGREES : FIFTEEN_DEGREES;
}

function rationalDegrees(degrees: number): Rational {
  return intR(degrees);
}

/** One chart angle, with every value exact. */
export function unitCircleAngle(
  degrees: number,
  undefinedText = "undefined"
): UnitCircleAngle {
  const exact = exactTrigValues(rationalDegrees(degrees)) as ExactTrig;
  const radians = piMultipleText(divR(rationalDegrees(degrees), intR(180)));
  return {
    degrees,
    radians,
    quadrant: quadrantOf(rationalDegrees(degrees)),
    reference: toNumberR(referenceAngle(rationalDegrees(degrees))),
    cos: formatTrigValue(exact.cos, undefinedText),
    sin: formatTrigValue(exact.sin, undefinedText),
    tan: formatTrigValue(exact.tan, undefinedText),
    cosValue: trigValueNumber(exact.cos) ?? 0,
    sinValue: trigValueNumber(exact.sin) ?? 0,
    tanValue: trigDecimal("tan", degrees),
  };
}

export function unitCircleAngles(
  set: AngleSet,
  undefinedText = "undefined"
): UnitCircleAngle[] {
  return angleDegrees(set).map((degrees) =>
    unitCircleAngle(degrees, undefinedText)
  );
}

/** All six functions at a chart angle, as exact text and decimals, for the detail panel. */
export function allFunctions(
  degrees: number,
  undefinedText = "undefined"
): { fn: TrigFunction; exact: string; value: number | null }[] {
  const exact = exactTrigValues(rationalDegrees(degrees)) as ExactTrig;
  return (["sin", "cos", "tan", "csc", "sec", "cot"] as const).map((fn) => ({
    fn,
    exact: formatTrigValue(exact[fn], undefinedText),
    value: trigDecimal(fn, degrees),
  }));
}
